import React, { useMemo, useCallback, useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useModelStore, useChatInputStore, useCompanyStore, useAuthStore } from '@/store';
import ModelCard from '@/components/Models/ModelCard';
import useResponsiveness from '@/hooks/useResponsiveness';
import StatusContainer from '@/components/StatusContainer';
import { getExploreGridColumns } from '@/components/Explore/exploreGridColumns';
import { isModelLockedForUser } from '@/constants/utils/trial';

// Tag slug for image models - same as used in Models page
const IMAGE_TAG_SLUG = 'image-generators';

const createStyles = (colors, isXS, columns) => StyleSheet.create({
  container: {
    paddingHorizontal: isXS ? 16 : 24,
  },
  grid: {
    flexDirection: isXS ? 'column' : 'row',
    flexWrap: isXS ? undefined : 'wrap',
    marginHorizontal: isXS ? 0 : -6,
  },
  gridItem: {
    width: isXS ? '100%' : `${100 / columns}%`,
    paddingHorizontal: isXS ? 0 : 6,
    marginBottom: 12,
  },
  emptyContainer: {
    paddingVertical: 40,
  },
});

const ImageModelsSection = ({ onPlusRequired }) => {
  const { colors } = useTheme();
  const responsive = useResponsiveness();
  const { isXS } = responsive;
  const columns = getExploreGridColumns(responsive);
  const router = useRouter();
  const user = useAuthStore(state => state.user);
  
  const initializeImageGeneration = useChatInputStore(state => state.initializeImageGeneration);
  const getModelById = useModelStore(state => state.getModelById);
  
  // Get model data from store - same approach as Models page
  const getModelsByTag = useModelStore(state => state.getModelsByTag);
  const fetchModelsByTag = useModelStore(state => state.fetchModelsByTag);
  const isLoadingModelsByTag = useModelStore(state => state.isLoadingModelsByTag);
  const modelsByTag = useModelStore(state => state.modelsByTag);
  const getCompanyById = useCompanyStore(state => state.getCompanyById);
  const fetchModelCompanies = useCompanyStore(state => state.fetchModelCompanies);

  const styles = useMemo(() => createStyles(colors, isXS, columns), [colors, isXS, columns]);

  // Fetch image models on mount - same as Models page does for 'image' tag.
  // Companies hydrate the catalog ModelCard's provider/slug title.
  useEffect(() => {
    fetchModelsByTag(IMAGE_TAG_SLUG);
    fetchModelCompanies?.();
  }, [fetchModelsByTag, fetchModelCompanies]);

  // Get image generation models from the store
  const imageModels = useMemo(() => {
    const models = getModelsByTag(IMAGE_TAG_SLUG) || [];
    // Limit to 9 models so the responsive grid fills cleanly (e.g. 3x3).
    return models.slice(0, 9);
  }, [getModelsByTag, modelsByTag]);

  const handleModelPress = useCallback((modelId) => {
    // Image-only models are free; video/other gated models need Plus or
    // paid credits. Anon visitors go through auth first via onPlusRequired.
    const model = getModelById(modelId);
    if (isModelLockedForUser(user, model)) {
      onPlusRequired?.(model);
      return;
    }
    
    // Initialize chat with this model selected for image generation.
    // Auth gating happens at the composer level when the user actually
    // sends; landing on `/app` with a model pre-selected (no prompt /
    // pendingAutoSend) is a public-friendly browse action.
    initializeImageGeneration('', modelId);
    router.push('/app');
  }, [initializeImageGeneration, router, onPlusRequired, getModelById, user]);

  if (isLoadingModelsByTag && imageModels.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.grid}>
          {[...Array(4)].map((_, index) => (
            <View key={index} style={styles.gridItem}>
              <ModelCard isLoading={true} />
            </View>
          ))}
        </View>
      </View>
    );
  }

  if (imageModels.length === 0) {
    return (
      <View style={styles.container}>
        <View style={styles.emptyContainer}>
          <StatusContainer
            type="empty"
            message="No image generation models available"
          />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.grid}>
        {imageModels.map((model) => (
          <View key={model.id} style={styles.gridItem}>
            <ModelCard
              model={model}
              company={getCompanyById(model.companyId)}
              onPress={handleModelPress}
            />
          </View>
        ))}
      </View>
    </View>
  );
};

export default React.memo(ImageModelsSection);

